import React from 'react';
import { Download, Sparkles, Zap, Shield, ArrowRight } from 'lucide-react';

const translations = {
  pt: {
    welcome: 'Bem-vindo ao',
    brand: 'MundoDigital24',
    tagline: 'Os melhores jogos móveis ao seu alcance',
    description: 'Descubra uma coleção incrível de jogos premium para o seu dispositivo móvel. Acesso ilimitado a centenas de jogos emocionantes, sem anúncios e sem complicações.',
    cta: 'Baixar Agora',
    explore: 'Ver Jogos',
    stats: {
      games: '100+ Jogos',
      downloads: 'Downloads Ilimitados',
      noAds: 'Sem Anúncios',
    }
  },
  en: {
    welcome: 'Welcome to',
    brand: 'MundoDigital24',
    tagline: 'Top mobile games at your fingertips',
    description: 'Discover an amazing collection of premium games for your mobile device. Unlimited access to hundreds of exciting games, no ads, no hassle.',
    cta: 'Download Now',
    explore: 'View Games',
    stats: {
      games: '100+ Games',
      downloads: 'Unlimited Downloads',
      noAds: 'No Ads',
    }
  }
};

function Hero({ language }) {
  const t = translations[language];

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-24 overflow-hidden">
      {/* Background Image with purple overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
        style={{ backgroundImage: 'url(/images/hero-mobile-games.jpg)' }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-b from-darker via-darker/95 to-darker"></div>
      
      {/* Purple gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10"></div>
      
      {/* Animated grid pattern */}
      <div className="absolute inset-0 grid-pattern opacity-30"></div>
      
      {/* Floating purple orbs */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-primary/20 rounded-full blur-2xl animate-float"></div>
      <div className="absolute top-1/3 right-20 w-40 h-40 bg-secondary/15 rounded-full blur-3xl animate-float animation-delay-400"></div>
      <div className="absolute bottom-1/4 left-1/4 w-36 h-36 bg-accent/10 rounded-full blur-2xl animate-float animation-delay-600"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left space-y-8 animate-slide-up">
            {/* Badge */}
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-primary/20 to-secondary/20 border border-primary/30 rounded-full backdrop-blur-sm">
              <Sparkles className="w-5 h-5 text-accent" />
              <span className="text-accent text-sm font-body font-bold tracking-wide uppercase">Premium Mobile Games</span>
            </div>
            
            {/* Main Heading */}
            <div className="space-y-4">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-black text-white leading-tight">
                {t.welcome}
                <span className="block gradient-text mt-2">{t.brand}</span>
              </h1>
              
              <p className="text-2xl sm:text-3xl text-accent font-body font-bold">
                {t.tagline}
              </p>
            </div>
            
            {/* Description */}
            <p className="text-slate-300 text-lg sm:text-xl font-body leading-relaxed max-w-xl mx-auto lg:mx-0">
              {t.description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
              <a 
                href="/s"
                className="group relative px-8 py-4 bg-gradient-to-r from-primary via-secondary to-primary bg-[length:200%_100%] hover:bg-right text-white font-body font-bold text-lg rounded-xl transition-all duration-500 shadow-purple-glow hover:shadow-neon-purple flex items-center justify-center gap-3"
              >
                <Download className="w-5 h-5 group-hover:scale-110 transition-transform" />
                {t.cta}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a 
                href="/s"
                className="px-8 py-4 border-2 border-primary/50 hover:border-primary text-white font-body font-semibold text-lg rounded-xl transition-all duration-300 hover:bg-primary/10 flex items-center justify-center gap-3"
              >
                <Zap className="w-5 h-5 text-accent" />
                {t.explore}
              </a>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-6 pt-8">
              {[
                { icon: Sparkles, label: t.stats.games, color: 'text-primary' },
                { icon: Download, label: t.stats.downloads, color: 'text-accent' },
                { icon: Shield, label: t.stats.noAds, color: 'text-secondary' },
              ].map((stat, index) => (
                <div key={index} className="flex flex-col items-center lg:items-start gap-2 p-4 bg-surface/50 rounded-xl border border-primary/20 backdrop-blur-sm">
                  <div className={`p-2 rounded-lg bg-primary/20 ${stat.color}`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                  <span className="text-white font-body font-bold text-sm text-center lg:text-left">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Visual - Image Container */}
          <div className="relative hidden lg:block">
            <div className="relative z-10">
              <div className="relative w-full max-w-lg mx-auto">
                {/* Main Image with purple glow */}
                <div className="relative rounded-3xl overflow-hidden shadow-purple-glow">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-secondary/20 to-accent/20 rounded-3xl blur-2xl"></div>
                  <div className="relative bg-surface rounded-3xl p-2 border border-primary/30">
                    <img 
                      src="/images/hero-mobile-games.jpg" 
                      alt="Mobile Games"
                      className="w-full h-auto object-cover rounded-2xl transform hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-darker/80 via-transparent to-transparent rounded-2xl"></div>
                  </div>
                </div>
                
                {/* Floating badges */}
                <div className="absolute -top-6 -right-6 px-5 py-3 bg-gradient-to-br from-primary to-secondary rounded-2xl shadow-neon-purple animate-float">
                  <span className="text-white font-heading font-bold text-sm">{t.stats.games}</span>
                </div>
                <div className="absolute -bottom-6 -left-6 px-5 py-3 bg-gradient-to-br from-secondary to-accent rounded-2xl shadow-neon-purple animate-float animation-delay-400">
                  <span className="text-white font-heading font-bold text-sm">{t.stats.noAds}</span>
                </div>
              </div>
            </div>
            
            {/* Background glow effect */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/20 rounded-full blur-3xl"></div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <div className="w-6 h-10 border-2 border-primary/50 rounded-full flex justify-center">
          <div className="w-1.5 h-3 bg-primary rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
